// API client for the AUTOXIUM calculator tools (Battery Sizing, Motor Sizing, Traction Power,
// CRC, AUTOSAR E2E, Data ID, Data Converter). All calculation logic for these tools lives
// server-side, in the main backend — this file only makes requests and returns response
// data. These endpoints are public (no auth token attached) by design.
import axios from 'axios';

const TOOLS_API_URL = process.env.REACT_APP_BACKEND_URL + '/api/tools';

async function post(path, body) {
  try {
    const response = await axios.post(`${TOOLS_API_URL}${path}`, body);
    return response.data;
  } catch (err) {
    const message = err.response?.data?.error || err.message || 'Request failed.';
    throw new Error(message);
  }
}

async function get(path) {
  const response = await axios.get(`${TOOLS_API_URL}${path}`);
  return response.data;
}

export const batteryToolApi = {
  sizeBattery: (rawValues) => post('/battery-sizing', rawValues),
};

export const motorToolApi = {
  sizeMotor: (rawValues) => post('/motor-sizing', rawValues),
};

export const tractionPowerToolApi = {
  calculate: (rawValues) => post('/traction-power', rawValues),
};

export const brakingDistanceToolApi = {
  calculate: (payload) => post('/braking-distance', payload),
};

export const gradientForceToolApi = {
  calculate: (payload) => post('/gradient-force', payload),
};

export const rollingResistanceToolApi = {
  calculate: (payload) => post('/rolling-resistance', payload),
};

export const aerodynamicForceToolApi = {
  calculate: (payload) => post('/aerodynamic-force', payload),
};

export const cableSizeToolApi = {
  calculate: (rawValues) => post('/cable-size', rawValues),
};

export const fuseRatingToolApi = {
  calculate: (rawValues) => post('/fuse-rating', rawValues),
};

export const voltageDropToolApi = {
  calculate: (rawValues) => post('/voltage-drop', rawValues),
};

// The precharge endpoint returns HTTP 422 with an {errors, error} body for invalid input
// (an infeasible design comes back as HTTP 200 {errors: {design}}); hand that body back (like the other tools' {errors}) so the
// component can show per-field messages instead of a generic failure.
export const prechargeToolApi = {
  calculate: async (payload) => {
    try {
      const response = await axios.post(`${TOOLS_API_URL}/precharge-resistor`, payload);
      return response.data;
    } catch (err) {
      if (err.response?.status === 422 && err.response.data?.errors) return err.response.data;
      throw new Error(err.response?.data?.error || err.message || 'Request failed.');
    }
  },
};

// Same contract as the precharge endpoint: HTTP 422 {errors, error} for invalid input,
// HTTP 200 {errors: {design}} for an infeasible design.
export const busbarToolApi = {
  calculate: async (payload) => {
    try {
      const response = await axios.post(`${TOOLS_API_URL}/busbar-sizing`, payload);
      return response.data;
    } catch (err) {
      if (err.response?.status === 422 && err.response.data?.errors) return err.response.data;
      throw new Error(err.response?.data?.error || err.message || 'Request failed.');
    }
  },
};

// Bad values inside a frame come back as HTTP 200 (per-frame field errors + validation
// issues). Only a malformed request is a 422; unwrap it like prechargeToolApi does.
// Shared by the CAN Raw Frame Decoder and the J1939 Frame Decoder.
const frameDecoderApi = (path) => ({
  run: async (payload) => {
    try {
      const response = await axios.post(`${TOOLS_API_URL}${path}`, payload);
      return response.data;
    } catch (err) {
      if (err.response?.status === 422 && err.response.data?.errors) return err.response.data;
      // Rate limited: tell the caller how long to wait so it can retry instead of failing.
      if (err.response?.status === 429) {
        const retryAfter = Number(err.response.headers?.['retry-after']);
        return { rateLimited: true, retryAfterS: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 10 };
      }
      throw new Error(err.response?.data?.error || err.message || 'Request failed.');
    }
  },
});

export const canFrameDecoderToolApi = frameDecoderApi('/can-frame-decoder');
export const j1939FrameDecoderToolApi = frameDecoderApi('/j1939-frame-decoder');

export const dcdcSizingToolApi = {
  calculate: (payload) => post('/dcdc-sizing', payload),
};

export const vehicleSpeedRpmToolApi = {
  convert: (rawValues) => post('/vehicle-speed-rpm', rawValues),
};

export const gearRatioToolApi = {
  calculate: (payload) => post('/gear-ratio', payload),
};

export const tyreSizeToolApi = {
  calculate: (payload) => post('/tyre-size', payload),
};

export const powerSplitToolApi = {
  calculate: (payload) => post('/power-split', payload),
};

export const torquePowerRpmToolApi = {
  calculate: (payload) => post('/torque-power-rpm', payload),
};

export const rotationalSpeedToolApi = {
  convert: (rawValues) => post('/rotational-speed', rawValues),
};

export const canBusLoadToolApi = {
  convert: (payload) => post('/can-bus-load', payload),
};

export const kwHpToolApi = {
  convert: (rawValues) => post('/kw-hp', rawValues),
};

export const torqueToolApi = {
  convert: (rawValues) => post('/torque', rawValues),
};

export const pressureToolApi = {
  convert: (rawValues) => post('/pressure', rawValues),
};

export const speedToolApi = {
  convert: (rawValues) => post('/speed', rawValues),
};

export const temperatureToolApi = {
  convert: (rawValues) => post('/temperature', rawValues),
};

export const fuelConsumptionToolApi = {
  convert: (rawValues) => post('/fuel-consumption', rawValues),
};

export const crcToolApi = {
  getPresets: () => get('/crc/presets'),
  computeCrc: (data, params) => post('/crc', { data, ...params }),
};

export const e2eToolApi = {
  getProfile1Modes: () => get('/e2e/profile1-modes'),
  protect: (profile, data, opts) => post(`/e2e/${profile}/protect`, { data, ...opts }),
  check: (profile, data, opts) => post(`/e2e/${profile}/check`, { data, ...opts }),
};

export const dataIdToolApi = {
  breakdown: (dataId, counter) => post('/data-id', { dataId, counter }),
};

export const converterToolApi = {
  convert: (value, base, width) => post('/convert', { value, base, width }),
};
